"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.PAGE_SPLIT_PART_PREFIX = void 0;
exports.measurePageLiveBytes = measurePageLiveBytes;
exports.splitPageDocument = splitPageDocument;
exports.pageDocumentSplitDue = pageDocumentSplitDue;
const Y = __importStar(require("yjs"));
const page_document_maps_1 = require("./page-document-maps");
const page_large_data_policy_1 = require("./page-large-data-policy");
const page_part_directory_1 = require("./page-part-directory");
const page_part_planner_1 = require("./page-part-planner");
const page_part_rect_1 = require("./page-part-rect");
const page_part_summary_1 = require("./page-part-summary");
const page_read_source_1 = require("./page-read-source");
const page_structure_entry_1 = require("./page-structure-entry");
const page_structure_tree_1 = require("./page-structure-tree");
const page_structure_writer_1 = require("./page-structure-writer");
/** Prefix of the parts an upgrade splits an existing page into. */
exports.PAGE_SPLIT_PART_PREFIX = 'upgrade-';
function clone(value) {
    if (value instanceof Y.Text) {
        const copy = new Y.Text();
        copy.applyDelta(value.toDelta());
        return copy;
    }
    if (value instanceof Y.Map) {
        const copy = new Y.Map();
        for (const [key, inner] of value)
            copy.set(key, clone(inner));
        return copy;
    }
    if (value instanceof Y.Array) {
        const copy = new Y.Array();
        copy.push(value.toArray().map(clone));
        return copy;
    }
    return value;
}
function cloneFields(fields) {
    return clone(fields);
}
/**
 * Encoded live maps of part zero, rebuilt without retained Yjs history.
 * Uses the same client id and value cloning as the split's scratch document.
 */
function measurePageLiveBytes(head) {
    const live = new Y.Doc();
    live.clientID = 1;
    try {
        live.transact(() => {
            for (const name of head.share.keys()) {
                const target = live.getMap(name);
                for (const [key, value] of head.getMap(name))
                    target.set(key, clone(value));
            }
        });
        return Y.encodeStateAsUpdate(live).byteLength;
    }
    finally {
        live.destroy();
    }
}
/**
 * Splits a current-format single-document page in reading order, using the
 * part planner. Mutates head only when fields move out of part zero. New part
 * IDs name their first entry; returned documents contain only the new parts.
 */
function splitPageDocument(head, policy = page_large_data_policy_1.PAGE_LARGE_DATA_POLICY.part, createPart = () => new Y.Doc(), existingParts = new Map()) {
    return planPageDocumentSplit(head, policy, createPart, existingParts, true)
        .parts;
}
/** Runs the split planner without changing the head or creating persisted parts. */
function pageDocumentSplitDue(head, policy = page_large_data_policy_1.PAGE_LARGE_DATA_POLICY.part) {
    try {
        return planPageDocumentSplit(head, policy, () => new Y.Doc(), new Map(), false).wouldMove;
    }
    catch (error) {
        if (error instanceof page_structure_writer_1.PageStructureWriteError ||
            error instanceof page_part_rect_1.PagePartRectError)
            return false;
        throw error;
    }
}
function planPageDocumentSplit(head, policy, createPart, existingParts, commit) {
    const formatVersion = (0, page_document_maps_1.readPageFormatVersion)(head);
    if (formatVersion !== page_structure_writer_1.PAGE_FORMAT_VERSION)
        throw new page_structure_writer_1.PageStructureWriteError('Unsupported page format for page split');
    const sourceStructure = (0, page_document_maps_1.pageStructureMap)(head);
    const sourceEntries = (0, page_read_source_1.readPage)((0, page_read_source_1.pageReadSource)(head, existingParts)).entries;
    if ([...(0, page_part_directory_1.readPagePartDirectory)(head).keys()].some(id => id.startsWith(exports.PAGE_SPLIT_PART_PREFIX)) ||
        [...sourceEntries.values()].some(entry => entry.s.startsWith(exports.PAGE_SPLIT_PART_PREFIX)))
        throw new page_structure_writer_1.PageStructureWriteError('Page is already split');
    const sourceBlocks = (0, page_document_maps_1.pagePartBlocksMap)(head);
    const sourceElements = (0, page_document_maps_1.pagePartElementsMap)(head);
    const tree = (0, page_structure_tree_1.derivePageTree)(sourceEntries);
    const scratchHead = new Y.Doc();
    scratchHead.clientID = 1;
    const scratchDocs = new Map([
        [page_document_maps_1.PAGE_DOCUMENT_PART_ID, scratchHead],
    ]);
    const parts = new Map();
    try {
        const moved = new Set([
            sourceBlocks,
            sourceElements,
            sourceStructure,
        ]);
        scratchHead.transact(() => {
            for (const [name, type] of head.share) {
                if (moved.has(type))
                    continue;
                // Top-level page metadata is stored in maps; resolve a decoded root
                // through getMap before cloning its values.
                const target = scratchHead.getMap(name);
                for (const [key, value] of head.getMap(name))
                    target.set(key, clone(value));
            }
        });
        const directory = new Map([
            ...[...(0, page_part_directory_1.readPagePartDirectory)(head)]
                .filter(([id]) => id !== page_document_maps_1.PAGE_DOCUMENT_PART_ID)
                .map(([id, value]) => [id, { ...value, b: policy.hardBytes }]),
            [page_document_maps_1.PAGE_DOCUMENT_PART_ID, { b: measurePageLiveBytes(scratchHead) }],
        ]);
        const entries = new Map();
        const place = (partId, write) => {
            let doc = scratchDocs.get(partId);
            if (!doc) {
                doc = new Y.Doc();
                doc.clientID = 1;
                scratchDocs.set(partId, doc);
            }
            const before = Y.encodeStateVector(doc);
            doc.transact(() => write(doc));
            const delta = Y.encodeStateAsUpdate(doc, before).byteLength;
            const previous = directory.get(partId);
            directory.set(partId, { ...previous, b: (previous?.b ?? 0) + delta });
        };
        const placeEntry = (id, entry) => {
            entries.set(id, entry);
            place(entry.s, doc => (0, page_document_maps_1.pageStructureMap)(doc).set(id, (0, page_structure_entry_1.encodePageStructureEntry)(entry)));
        };
        const fieldsOf = (id, element) => {
            const fields = (element ? sourceElements : sourceBlocks).get(id);
            if (!fields)
                throw new page_structure_writer_1.PageStructureWriteError('Missing page fields: ' + id);
            return fields;
        };
        const placeBlock = (blockId, parentId, siblings, holdSubtree = false) => {
            const entry = sourceEntries.get(blockId);
            const fields = sourceBlocks.get(blockId);
            // A fieldless block cannot expose a child frontier. Keep its descendants
            // in the head too, while existing client parts remain untouched.
            const holdChildren = holdSubtree || (entry.s === page_document_maps_1.PAGE_DOCUMENT_PART_ID && !fields);
            const keepInHead = holdChildren || tree.parentOf.get(blockId) === page_structure_tree_1.PAGE_TREE_RECOVERED_ID;
            const { partId } = entry.s !== page_document_maps_1.PAGE_DOCUMENT_PART_ID
                ? { partId: entry.s }
                : parentId === null || keepInHead
                    ? { partId: page_document_maps_1.PAGE_DOCUMENT_PART_ID }
                    : (0, page_part_planner_1.planPageBlockPart)({
                        formatVersion,
                        entries,
                        directory,
                        parentId,
                        siblings,
                        writerPrefix: exports.PAGE_SPLIT_PART_PREFIX,
                        newPartId: () => exports.PAGE_SPLIT_PART_PREFIX + blockId,
                        policy,
                    });
            if (entry.s !== page_document_maps_1.PAGE_DOCUMENT_PART_ID) {
                entries.set(blockId, entry);
            }
            else {
                placeEntry(blockId, { ...entry, s: partId });
                if (fields)
                    place(partId, doc => (0, page_document_maps_1.pagePartBlocksMap)(doc).set(blockId, cloneFields(fields)));
            }
            const placed = [];
            // Recovered is a synthetic tree node, never a stored block.
            const children = (tree.children.get(blockId) ?? []).flatMap(id => id === page_structure_tree_1.PAGE_TREE_RECOVERED_ID ? (tree.children.get(id) ?? []) : [id]);
            for (const childId of children) {
                placeBlock(childId, blockId, placed, holdChildren);
                placed.push(childId);
            }
            const elementIds = (tree.elements.get(blockId) ?? []).filter(id => {
                const entry = sourceEntries.get(id);
                if (entry.s !== page_document_maps_1.PAGE_DOCUMENT_PART_ID) {
                    entries.set(id, entry);
                    return false;
                }
                const fields = sourceElements.get(id);
                // blockId is this element's parent in the derived tree.
                if (holdChildren || !fields || entry.p !== blockId) {
                    placeEntry(id, entry);
                    if (fields)
                        place(page_document_maps_1.PAGE_DOCUMENT_PART_ID, doc => (0, page_document_maps_1.pagePartElementsMap)(doc).set(id, cloneFields(fields)));
                    return false;
                }
                return true;
            });
            const places = new Map();
            for (const id of elementIds) {
                const rect = (0, page_part_rect_1.pagePartEntryPlace)(page_document_maps_1.PAGE_DOCUMENT_PART_ID, id, fieldsOf(id, true), sourceEntries);
                if (rect)
                    places.set(id, rect);
            }
            const placeElement = (id, rect) => {
                const plan = (0, page_part_planner_1.planPageElementPart)({
                    formatVersion,
                    rect,
                    entries,
                    directory,
                    writerPrefix: exports.PAGE_SPLIT_PART_PREFIX,
                    newPartId: () => exports.PAGE_SPLIT_PART_PREFIX + id,
                    policy,
                });
                placeEntry(id, { ...sourceEntries.get(id), s: plan.partId });
                place(plan.partId, doc => (0, page_document_maps_1.pagePartElementsMap)(doc).set(id, cloneFields(fieldsOf(id, true))));
                directory.set(plan.partId, {
                    ...directory.get(plan.partId),
                    r: plan.rect,
                });
            };
            for (const id of elementIds) {
                const rect = places.get(id);
                if (rect)
                    placeElement(id, rect);
            }
            for (const id of elementIds) {
                if (places.has(id))
                    continue;
                const entry = sourceEntries.get(id);
                const rect = entry.k === page_structure_entry_1.PAGE_CONNECTOR_KIND
                    ? (places.get(entry.c?.[0] ?? '') ?? places.get(entry.c?.[1] ?? ''))
                    : undefined;
                placeElement(id, rect ?? [0, 0, 0, 0]);
            }
        };
        if (tree.root !== null)
            placeBlock(tree.root, null, []);
        if (![...entries].some(([id, entry]) => sourceEntries.get(id).s === page_document_maps_1.PAGE_DOCUMENT_PART_ID &&
            entry.s !== page_document_maps_1.PAGE_DOCUMENT_PART_ID))
            return { wouldMove: false, parts: null };
        if (!commit)
            return { wouldMove: true, parts: null };
        head.transact(() => {
            for (const [id, entry] of entries) {
                if (entry.s === page_document_maps_1.PAGE_DOCUMENT_PART_ID ||
                    sourceEntries.get(id).s !== page_document_maps_1.PAGE_DOCUMENT_PART_ID)
                    continue;
                let part = parts.get(entry.s);
                if (!part) {
                    part = createPart(entry.s);
                    parts.set(entry.s, part);
                }
                const element = (0, page_structure_entry_1.isPageElementKind)(entry.k);
                const source = element ? sourceElements : sourceBlocks;
                const target = element
                    ? (0, page_document_maps_1.pagePartElementsMap)(part)
                    : (0, page_document_maps_1.pagePartBlocksMap)(part);
                target.set(id, cloneFields(fieldsOf(id, element)));
                source.delete(id);
                (0, page_document_maps_1.pageStructureMap)(part).set(id, (0, page_structure_entry_1.encodePageStructureEntry)(entry));
                sourceStructure.delete(id);
            }
        });
        const finalEntries = (0, page_read_source_1.readPage)((0, page_read_source_1.pageReadSource)(head, new Map([...existingParts, ...parts]))).entries;
        (0, page_part_directory_1.applyPagePartDirectoryChanges)(head, [[page_document_maps_1.PAGE_DOCUMENT_PART_ID, head], ...parts].map(([partId, doc]) => ({
            kind: 'set',
            partId,
            value: {
                b: Y.encodeStateAsUpdate(doc).byteLength,
                m: (0, page_part_summary_1.summarizePagePart)(new Map([...finalEntries].filter(([, entry]) => entry.s === partId))),
                ...(directory.get(partId)?.r === undefined
                    ? {}
                    : { r: directory.get(partId).r }),
            },
        })));
        return { wouldMove: true, parts };
    }
    catch (error) {
        for (const part of parts.values())
            part.destroy();
        throw error;
    }
    finally {
        for (const doc of scratchDocs.values())
            doc.destroy();
    }
}
